import React from "react"
import { ReactComponent as BuildingIcon } from "@ciq/shared/assets/icons/map/building_icon.svg"
import { ReactComponent as FloorIcon } from "@ciq/shared/assets/icons/map/floor_icon.svg"

/**
 * SVG chevron icon for building expand/collapse.
 *
 * @param {Object} props
 * @param {string} [props.className] - CSS class names
 * @returns {React.ReactElement} Chevron SVG
 */
function ChevronIcon({ className }) {
  return (
    <svg className={className} width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

/**
 * @typedef {Object} SidebarItem
 * @property {string} id - Unique identifier for the sidebar item
 * @property {string} label - Display label for the item
 * @property {string} [type] - Type of item: "inbox", "building", "floor"
 */

/**
 * Sidebar for the File Upload page showing building/floor tree.
 * Supports drop targets for file-to-building drag-and-drop when handlers are provided.
 *
 * @param {Object} props
 * @param {string} props.selectedItemId - Currently selected sidebar item ID
 * @param {Function} props.onSelectItem - Callback when sidebar item is selected
 * @param {Array<SidebarItem>} props.items - Sidebar items to display
 * @param {Array<string>} [props.expandedBuildings] - IDs of expanded buildings
 * @param {Function} [props.onToggleBuilding] - Callback for building expand/collapse
 * @param {string|null} [props.activeDropTargetId] - ID of the building or floor being hovered during drag
 * @param {Function} [props.onBuildingDragEnter] - Callback when drag enters a building or floor
 * @param {Function} [props.onBuildingDragLeave] - Callback when drag leaves a building or floor
 * @param {Function} [props.onBuildingDragOver] - Callback for dragOver (must preventDefault) on building or floor
 * @param {Function} [props.onBuildingDrop] - Callback when files dropped on building or floor
 * @returns {React.ReactElement} The sidebar component
 */
export function Sidebar({
  selectedItemId,
  onSelectItem,
  items,
  expandedBuildings = [],
  onToggleBuilding,
  activeDropTargetId = null,
  onBuildingDragEnter,
  onBuildingDragLeave,
  onBuildingDragOver,
  onBuildingDrop,
}) {
  const safeItems = items || []

  const renderPlacementIndicator = placementStatus => {
    switch (placementStatus) {
      case "placed":
        return (
          <span className="file-upload-placement-indicator indicator-placed" aria-label="Placed">
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
              <path d="M1.5 5l2.5 2.5 4.5-5" stroke="var(--green-color-500)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
        )
      case "not-placed":
        return (
          <span
            className="file-upload-placement-indicator indicator-not-placed"
            aria-label="Not placed"
          />
        )
      case "none":
      default:
        return (
          <span
            className="file-upload-placement-indicator indicator-none"
            aria-label="No upload"
          />
        )
    }
  }

  const renderedItems = safeItems.flatMap(item => {
    const hasFloors = item.type === "building" && Array.isArray(item.floors) && item.floors.length > 0
    const isExpanded = hasFloors && expandedBuildings.includes(item.id)
    const floors = isExpanded ? item.floors : []

    return [item, ...floors]
  })

  return (
    <div className="file-upload-sidebar">
      <div className="file-upload-sidebar-content" role="list" aria-label="File upload navigation">
        {renderedItems.map(item => {
          const isSelected = selectedItemId === item.id
          const hasFloors = item.type === "building" && Array.isArray(item.floors) && item.floors.length > 0
          const isExpanded = hasFloors && expandedBuildings.includes(item.id)
          const isFloor = item.type === "floor"
          const isDropTarget = item.type === "building" || item.type === "floor"
          const isActiveTarget = activeDropTargetId === item.id

          const itemClassName = [
            "file-upload-sidebar-item",
            isSelected && "selected",
            isFloor && "floor-item",
            isDropTarget && isActiveTarget && "drop-target-active",
          ].filter(Boolean).join(" ")

          if (hasFloors) {
            return (
              <div
                key={item.id}
                data-testid={`building-row-${item.id}`}
                className={itemClassName}
                role="listitem"
                onDragEnter={isDropTarget && onBuildingDragEnter ? () => onBuildingDragEnter(item.id) : undefined}
                onDragLeave={isDropTarget && onBuildingDragLeave ? () => onBuildingDragLeave(item.id) : undefined}
                onDragOver={isDropTarget && onBuildingDragOver ? onBuildingDragOver : undefined}
                onDrop={isDropTarget && onBuildingDrop ? (e) => onBuildingDrop(item.id, e) : undefined}
              >
                <button
                  type="button"
                  aria-expanded={isExpanded}
                  onClick={() => {
                    onSelectItem && onSelectItem(item.id)
                    onToggleBuilding && onToggleBuilding(item.id)
                  }}
                >
                  <ChevronIcon className={["sidebar-chevron", isExpanded && "sidebar-chevron--expanded"].filter(Boolean).join(" ")} />
                  <BuildingIcon className="sidebar-building-icon" data-testid={`building-icon-${item.id}`} style={{ color: item.iconColor }} />
                  <span>{item.label}</span>
                  {item.fileCount > 0 && (
                    <span className="file-upload-badge">{item.fileCount}</span>
                  )}
                </button>
              </div>
            )
          }

          if (isFloor) {
            return (
              <div
                key={item.id}
                className={itemClassName}
                role="listitem"
                onDragEnter={isDropTarget && onBuildingDragEnter ? () => onBuildingDragEnter(item.id) : undefined}
                onDragLeave={isDropTarget && onBuildingDragLeave ? () => onBuildingDragLeave(item.id) : undefined}
                onDragOver={isDropTarget && onBuildingDragOver ? onBuildingDragOver : undefined}
                onDrop={isDropTarget && onBuildingDrop ? (e) => onBuildingDrop(item.id, e) : undefined}
              >
                <button type="button" aria-pressed={isSelected} onClick={() => onSelectItem && onSelectItem(item.id)}>
                  <span className="sidebar-chevron-spacer" aria-hidden="true" />
                  <FloorIcon className="sidebar-floor-icon" data-testid={`floor-icon-${item.id}`} style={{ color: item.iconColor }} />
                  <span>{item.label}</span>
                  {renderPlacementIndicator(item.placementStatus)}
                </button>
              </div>
            )
          }

          if (item.type === "building") {
            return (
              <div
                key={item.id}
                data-testid={`building-row-${item.id}`}
                className={itemClassName}
                role="listitem"
                onDragEnter={isDropTarget && onBuildingDragEnter ? () => onBuildingDragEnter(item.id) : undefined}
                onDragLeave={isDropTarget && onBuildingDragLeave ? () => onBuildingDragLeave(item.id) : undefined}
                onDragOver={isDropTarget && onBuildingDragOver ? onBuildingDragOver : undefined}
                onDrop={isDropTarget && onBuildingDrop ? (e) => onBuildingDrop(item.id, e) : undefined}
              >
                <button type="button"
                  aria-pressed={isSelected}
                  onClick={() => onSelectItem && onSelectItem(item.id)}
                >
                  <span className="sidebar-chevron-spacer" aria-hidden="true" />
                  <BuildingIcon className="sidebar-building-icon" data-testid={`building-icon-${item.id}`} style={{ color: item.iconColor }} />
                  <span>{item.label}</span>
                  {item.fileCount > 0 && (
                    <span className="file-upload-badge">{item.fileCount}</span>
                  )}
                </button>
              </div>
            )
          }

          return (
            <div
              key={item.id}
              className={itemClassName}
              role="listitem"
              onDragEnter={isDropTarget && onBuildingDragEnter ? () => onBuildingDragEnter(item.id) : undefined}
              onDragLeave={isDropTarget && onBuildingDragLeave ? () => onBuildingDragLeave(item.id) : undefined}
              onDragOver={isDropTarget && onBuildingDragOver ? onBuildingDragOver : undefined}
              onDrop={isDropTarget && onBuildingDrop ? (e) => onBuildingDrop(item.id, e) : undefined}
            >
              <button type="button"
                aria-pressed={isSelected}
                onClick={() => onSelectItem && onSelectItem(item.id)}
              >
                <span>{item.label}</span>
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}
